import { X, Cookie, Settings, Check } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// Cookie preferences interface
export interface CookiePreferences {
  necessary: boolean; // Always true, can't be disabled
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

// Default preferences
const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
};

// Cookie consent storage key
const CONSENT_STORAGE_KEY = 'gulart_cookie_consent';
const PREFERENCES_STORAGE_KEY = 'gulart_cookie_preferences';

// Get stored consent
export const getStoredConsent = (): boolean | null => {
  const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
  return stored ? JSON.parse(stored) : null;
};

// Get stored preferences
export const getStoredPreferences = (): CookiePreferences | null => {
  const stored = localStorage.getItem(PREFERENCES_STORAGE_KEY);
  return stored ? JSON.parse(stored) : null;
};

// Check if analytics is allowed
export const isAnalyticsAllowed = (): boolean => {
  const prefs = getStoredPreferences();
  return prefs?.analytics ?? false;
};

// Check if marketing is allowed
export const isMarketingAllowed = (): boolean => {
  const prefs = getStoredPreferences();
  return prefs?.marketing ?? false;
};

interface CookieConsentProps {
  onConsentChange?: (preferences: CookiePreferences) => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ onConsentChange }) => {
  const { t } = useTranslation('common');
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] =
    useState<CookiePreferences>(DEFAULT_PREFERENCES);

  // Check for existing consent on mount
  useEffect(() => {
    const hasConsent = getStoredConsent();
    const storedPrefs = getStoredPreferences();

    if (hasConsent === null) {
      // No consent given yet, show banner
      setIsVisible(true);
    } else if (storedPrefs) {
      // Load stored preferences
      setPreferences(storedPrefs);
    }
  }, []);

  // Save consent and preferences
  const saveConsent = useCallback(
    (prefs: CookiePreferences) => {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(true));
      localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(prefs));
      setPreferences(prefs);
      setIsVisible(false);
      onConsentChange?.(prefs);

      // Initialize analytics if allowed
      if (prefs.analytics && typeof window !== 'undefined') {
        // Initialize Google Analytics or other analytics
        console.log('Analytics enabled');
      }
    },
    [onConsentChange]
  );

  // Accept all cookies
  const handleAcceptAll = useCallback(() => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    saveConsent(allAccepted);
  }, [saveConsent]);

  // Accept only necessary cookies
  const handleAcceptNecessary = useCallback(() => {
    saveConsent(DEFAULT_PREFERENCES);
  }, [saveConsent]);

  // Save custom preferences
  const handleSavePreferences = useCallback(() => {
    saveConsent(preferences);
    setShowSettings(false);
  }, [preferences, saveConsent]);

  // Toggle preference
  const togglePreference = useCallback((key: keyof CookiePreferences) => {
    if (key === 'necessary') return; // Can't toggle necessary cookies
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  // Reopen settings (can be called from footer link)
  const reopenSettings = useCallback(() => {
    setIsVisible(true);
    setShowSettings(true);
  }, []);

  // Expose reopen function globally
  useEffect(() => {
    (window as unknown as Record<string, unknown>).openCookieSettings =
      reopenSettings;
    return () => {
      delete (window as unknown as Record<string, unknown>).openCookieSettings;
    };
  }, [reopenSettings]);

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay (z-cookie, banner sits above via DOM order) */}
      <div className="fixed inset-0 bg-ink/50 z-cookie" />

      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-cookie p-4 md:p-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-ivory border border-ink shadow-soft-xl">
            {!showSettings ? (
              // Main consent view
              <div className="p-7 sm:p-8">
                <p className="text-[10px] uppercase tracking-[0.22em] text-rust mb-3">
                  {t('cookieConsent.labelMain')}
                </p>
                <h2 className="font-display text-[28px] sm:text-[32px] leading-[1.1] tracking-[-0.015em] text-ink mb-4">
                  {t('cookieConsent.headingPrefix')}{' '}
                  <span
                    className="italic text-rust"
                    style={{ fontStyle: 'italic' }}
                  >
                    {t('cookieConsent.headingEmphasis')}
                  </span>
                  {t('cookieConsent.headingSuffix')}
                </h2>
                <p className="text-[14px] leading-[1.65] text-ink-2 mb-3">
                  {t('cookieConsent.bodyText')}
                </p>
                <p className="text-[12px] text-ink-3 mb-7">
                  {t('cookieConsent.readMorePrefix')}{' '}
                  <Link
                    to="/privacy"
                    className="text-ink hover:text-rust underline underline-offset-4 transition-colors inline-flex items-center min-h-[44px] md:min-h-0 py-1"
                  >
                    {t('cookieConsent.privacyPolicyLink')}
                  </Link>
                  .
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 px-6 py-3.5 bg-ink text-ivory text-[11px] uppercase tracking-[0.18em] font-semibold hover:bg-rust transition-colors inline-flex items-center justify-center gap-2"
                  >
                    <Check className="w-3.5 h-3.5" strokeWidth={1.75} />
                    {t('cookieConsent.acceptAll')}
                  </button>
                  <button
                    onClick={handleAcceptNecessary}
                    className="flex-1 px-6 py-3.5 border border-ink text-ink text-[11px] uppercase tracking-[0.18em] font-semibold hover:bg-ivory-2 transition-colors"
                  >
                    {t('cookieConsent.necessaryOnly')}
                  </button>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="flex-1 px-6 py-3.5 border border-line text-ink-2 text-[11px] uppercase tracking-[0.18em] font-semibold hover:border-ink hover:text-ink transition-colors inline-flex items-center justify-center gap-2"
                  >
                    <Settings className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {t('cookieConsent.settings')}
                  </button>
                </div>
              </div>
            ) : (
              // Settings view
              <div className="p-7 sm:p-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-rust mb-2">
                      {t('cookieConsent.settingsLabel')}
                    </p>
                    <h2 className="font-display text-[26px] leading-[1.1] tracking-[-0.015em] text-ink">
                      {t('cookieConsent.settingsHeading')}
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="w-9 h-9 flex items-center justify-center text-ink-2 hover:text-rust transition-colors"
                    aria-label={t('cookieConsent.closeAriaLabel')}
                  >
                    <X className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>

                <div className="border-t border-line">
                  {/* Necessary cookies */}
                  <div className="py-5 border-b border-line">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="font-display text-[18px] tracking-[-0.01em] text-ink">
                          {t('cookieConsent.necessaryTitle')}
                        </h3>
                        <p className="text-[10px] uppercase tracking-[0.22em] text-rust mt-0.5">
                          {t('cookieConsent.necessaryRequired')}
                        </p>
                      </div>
                      <div className="w-10 h-5 bg-ink flex items-center justify-end px-0.5">
                        <div className="w-4 h-4 bg-ivory" />
                      </div>
                    </div>
                    <p className="text-[12px] text-ink-2 leading-relaxed">
                      {t('cookieConsent.necessaryDesc')}
                    </p>
                  </div>

                  {/* Analytics */}
                  <div className="py-5 border-b border-line">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-display text-[18px] tracking-[-0.01em] text-ink">
                        {t('cookieConsent.analyticsTitle')}
                      </h3>
                      <button
                        onClick={() => togglePreference('analytics')}
                        className={`w-10 h-5 flex items-center px-0.5 transition-colors ${
                          preferences.analytics
                            ? 'bg-ink justify-end'
                            : 'bg-ivory-2 border border-line justify-start'
                        }`}
                      >
                        <div
                          className={`w-4 h-4 ${
                            preferences.analytics ? 'bg-ivory' : 'bg-ink-3'
                          }`}
                        />
                      </button>
                    </div>
                    <p className="text-[12px] text-ink-2 leading-relaxed">
                      {t('cookieConsent.analyticsDesc')}
                    </p>
                  </div>

                  {/* Marketing */}
                  <div className="py-5 border-b border-line">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-display text-[18px] tracking-[-0.01em] text-ink">
                        {t('cookieConsent.marketingTitle')}
                      </h3>
                      <button
                        onClick={() => togglePreference('marketing')}
                        className={`w-10 h-5 flex items-center px-0.5 transition-colors ${
                          preferences.marketing
                            ? 'bg-ink justify-end'
                            : 'bg-ivory-2 border border-line justify-start'
                        }`}
                      >
                        <div
                          className={`w-4 h-4 ${
                            preferences.marketing ? 'bg-ivory' : 'bg-ink-3'
                          }`}
                        />
                      </button>
                    </div>
                    <p className="text-[12px] text-ink-2 leading-relaxed">
                      {t('cookieConsent.marketingDesc')}
                    </p>
                  </div>

                  {/* Preferences */}
                  <div className="py-5 border-b border-line">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-display text-[18px] tracking-[-0.01em] text-ink">
                        {t('cookieConsent.preferencesTitle')}
                      </h3>
                      <button
                        onClick={() => togglePreference('preferences')}
                        className={`w-10 h-5 flex items-center px-0.5 transition-colors ${
                          preferences.preferences
                            ? 'bg-ink justify-end'
                            : 'bg-ivory-2 border border-line justify-start'
                        }`}
                      >
                        <div
                          className={`w-4 h-4 ${
                            preferences.preferences ? 'bg-ivory' : 'bg-ink-3'
                          }`}
                        />
                      </button>
                    </div>
                    <p className="text-[12px] text-ink-2 leading-relaxed">
                      {t('cookieConsent.preferencesDesc')}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mt-7">
                  <button
                    onClick={handleSavePreferences}
                    className="flex-1 px-6 py-3.5 bg-ink text-ivory text-[11px] uppercase tracking-[0.18em] font-semibold hover:bg-rust transition-colors"
                  >
                    {t('cookieConsent.savePreferences')}
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 px-6 py-3.5 border border-ink text-ink text-[11px] uppercase tracking-[0.18em] font-semibold hover:bg-ivory-2 transition-colors"
                  >
                    {t('cookieConsent.acceptAllAlt')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Cookie settings button for footer
export const CookieSettingsButton: React.FC = () => {
  const { t } = useTranslation('common');
  const handleClick = () => {
    if (
      typeof window !== 'undefined' &&
      (window as unknown as Record<string, unknown>).openCookieSettings
    ) {
      (
        (window as unknown as Record<string, unknown>)
          .openCookieSettings as () => void
      )();
    }
  };

  return (
    <button
      onClick={handleClick}
      className="text-ink-3 hover:text-rust text-[11px] uppercase tracking-[0.18em] flex items-center gap-1.5 transition-colors"
    >
      <Cookie className="w-3.5 h-3.5" strokeWidth={1.5} />
      {t('cookieConsent.footerButton')}
    </button>
  );
};

export default CookieConsent;
